import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { apiFetch } from '../api';
import { isVaultRow } from './investments';
import StickerSlots from './StickerSlots';
import './NewItemsPrompt.css';

// "Where did this go?" — the question the server cannot answer for you.
//
// WHY THIS EXISTS
// An item leaving your Steam inventory means one of several things, and only
// one of them is provable. server/services/saleReconciler.js already closes the
// provable case on its own: item gone AND a settled CSFloat sale means the row
// moves to Sold with the real price, no question asked.
//
// Everything else lands here. Gone with no CSFloat sale could be a sale
// off-site, a trade, a gift, a move to storage, or a stale inventory reading —
// nothing in the data can tell those apart, so guessing would write a sale that
// never happened. The queue (pendingDisappearances) has existed all along; it
// was only ever surfaced inside the inventory import modal, which meant an item
// could sit unanswered indefinitely if you never opened that screen.
//
// So this is a banner that shows anywhere, on every tab, for as long as the
// queue is not empty. Vault items are named first and marked, because an
// investment vanishing is the case with the most money attached and the one you
// are least likely to remember the details of months later.

const shortDate = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? null
    : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' });
};

const nameOf = (item) => item?.market_hash_name || item?.marketHashName || item?.name || 'Unknown item';
const iconOf = (item) => item?.iconUrl || item?.icon_url || null;

/* ─── Banner ──────────────────────────────────────────────── */
export function VanishedItemsBanner({
  unanswered = [], explained = 0, vaultCount = 0, onReview, onDismiss, variant = 'classic',
}) {
  const n = unanswered.length;
  if (!n && !explained) return null;
  return (
    <div className={`nip-banner vanished ${variant}`}>
      <span className="nip-banner-ico" aria-hidden="true">{variant === 'zone' ? 'Gone' : ''}</span>
      <span className="nip-banner-txt">
        {n > 0 ? (
          <>
            <strong>{n} item{n !== 1 ? 's' : ''}</strong> left your inventory and CSFloat has no sale
            for {n !== 1 ? 'them' : 'it'}.
            {vaultCount > 0 && (
              <> <strong>{vaultCount}</strong> {vaultCount !== 1 ? 'were' : 'was'} in the vault.</>
            )}{' '}
            <button type="button" className="nip-banner-link" onClick={onReview}>
              Say what happened
            </button>
          </>
        ) : (
          <>
            <strong>{explained} sold item{explained !== 1 ? 's' : ''}</strong> {explained !== 1 ? 'are' : 'is'} on
            the way out of your inventory.{' '}
            <button type="button" className="nip-banner-link" onClick={onReview}>See them</button>
          </>
        )}
        <span className="nip-banner-sub">
          {n > 0 && <>Until you answer, {n !== 1 ? 'they stay' : 'it stays'} counted as something you still hold. </>}
          {/* Naming the settled ones matters: they are the difference between
              "the tracker missed my sales" and "the tracker is waiting for
              CSFloat", and only one of those is a problem. */}
          {explained > 0 && (
            <>{explained} more sold on CSFloat and {explained !== 1 ? 'are' : 'is'} settling — {explained !== 1 ? 'they file' : 'it files'} {explained !== 1 ? 'themselves' : 'itself'} once trade protection ends.</>
          )}
        </span>
      </span>
      <button type="button" className="nip-banner-x" onClick={onDismiss} aria-label="Dismiss">×</button>
    </div>
  );
}

/* ─── Modal ───────────────────────────────────────────────── */
function VanishedItemsModal({ items, unanswered = 0, onSell, onTrack, onResolve, onClose, variant = 'classic' }) {
  const body = (
    <div className={`nip-overlay ${variant}`} onClick={onClose}>
      <div className="nip-panel" onClick={e => e.stopPropagation()}>
        <div className="nip-head">
          <div className="nip-head-txt">
            <h2 className="nip-title">What happened to these?</h2>
            <p className="nip-sub">
              Each of these left your Steam inventory. The ones marked <em>settling</em> sold on
              CSFloat and are inside Steam's trade-protection window — they file themselves when
              it ends, and need nothing from you. The rest have no sale to explain them, and the
              tracker will not guess: until you answer, each stays counted as something you hold.
              An item you put in a storage unit is still yours, so say so rather than dismissing
              it: Steam gives it a new id when you take it back out, and only that answer lets it
              be recognised as the same item.
            </p>
          </div>
          <button type="button" className="nip-x" onClick={onClose} aria-label="Close">×</button>
        </div>

        <div className="nip-body">
          {items.map(entry => {
            const { item, row, realIndex, inVault, sale } = entry;
            const left = shortDate(item._disappearedAt);
            const icon = iconOf(item);
            // Float and pattern come off the row when there is one and off the
            // queued inventory copy when there is not — the item had them
            // either way, and they are how you recognise which copy this was.
            const float = row?.float ?? item.float;
            const seed  = row?.paintSeed ?? item.paintSeed;
            // Stickers are the other half of telling two copies apart. The
            // queued inventory copy is preferred because Steam gave it the
            // pictures; a row built from trade history often has names only.
            const stickers = item.stickers?.length ? item.stickers : (row?.stickers || []);
            const charms   = item.charms?.length   ? item.charms   : (row?.charms   || []);
            return (
              <div key={item.assetId} className={`vip-row${inVault ? ' vault' : ''}${sale ? ' settled' : ''}`}>
                {icon
                  ? <img className="vip-thumb" src={icon} alt="" />
                  : <div className="vip-thumb blank" />}

                <div className="vip-main">
                  <div className="vip-name">
                    {nameOf(item)}
                    {inVault && <span className="vip-flag">vault</span>}
                    {sale && <span className="vip-flag sold">settling</span>}
                    {!row && !sale && <span className="vip-flag muted">not tracked</span>}
                  </div>
                  <div className="vip-meta">
                    {left && <span>left {left}</span>}
                    {row && <span>cost ${(parseFloat(row.buyPrice) || 0).toFixed(2)}</span>}
                    {sale && <span className="vip-sold">sold ${sale.price.toFixed(2)} on CSFloat</span>}
                    {float != null && <span className="mono">{Number(float).toFixed(6)}</span>}
                    {seed != null && <span className="mono">#{seed}</span>}
                  </div>
                  <StickerSlots stickers={stickers} charms={charms} variant={variant}
                    maxWidth={240} style={{ marginTop: 8 }} />
                </div>

                <div className="vip-acts">
                  {sale ? (
                    // Nothing to ask and nothing to press. The sale is real but
                    // reversible until trade protection ends, and writing it
                    // early would put money in your books that can still go
                    // away. The server files it the moment it verifies.
                    <span className="vip-note">
                      Files itself{sale.settlesAt ? ` after ${shortDate(sale.settlesAt)}` : ' once it settles'}.
                    </span>
                  ) : row ? (
                    // One button, not two: the sold/traded modal already asks
                    // which of the two it was, and asking here as well would be
                    // the same question in two places, able to disagree.
                    <>
                      <button type="button" className="vip-btn primary"
                        onClick={() => onSell(row, realIndex, item.assetId)}>
                        Sold or traded…
                      </button>
                      <button type="button" className="vip-btn"
                        title="It is still yours — a storage unit, a stale reading, or it came back"
                        onClick={() => onResolve([item.assetId])}>
                        Still mine
                      </button>
                    </>
                  ) : (
                    // Nothing to close, but two different reasons to stop
                    // asking. Storage is not a dismissal: the item is still
                    // yours and will come back with a new assetId, so the
                    // answer is kept and the return recognised. Dismiss forgets
                    // the question entirely.
                    // Never tracked, which is not the same as nothing to do.
                    // Selling something you never got round to adding is the
                    // ordinary way an untracked item leaves, and this used to
                    // offer only storage or dismissal — so the one answer people
                    // actually had was the one answer they could not give, and
                    // the sale went unrecorded. Tracking it builds the row from
                    // what Steam still knows (name, wear, float, pattern) and
                    // opens the same sale modal a tracked item gets.
                    <>
                      <button type="button" className="vip-btn primary"
                        title="Record what you paid and what you sold it for"
                        onClick={() => onTrack(item)}>
                        Sold or traded…
                      </button>
                      <button type="button" className="vip-btn"
                        title="It went into a storage unit: still yours, Steam just stops showing it"
                        onClick={() => onResolve([item.assetId], 'storage')}>
                        In storage
                      </button>
                      <button type="button" className="vip-btn"
                        onClick={() => onResolve([item.assetId])}>
                        Dismiss
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className="nip-foot">
          <span className="nip-note">
            <strong>{unanswered}</strong> unanswered
            {items.length > unanswered && <> · {items.length - unanswered} settling on CSFloat</>}
          </span>
          <div className="nip-foot-end">
            <button type="button" className="nip-dismiss" onClick={onClose}>
              {unanswered ? 'Answer later' : 'Close'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(body, document.body);
}

/* ─── The pair, wired to the server queue ─────────────────── */
/**
 * @param {string}   user        account name, for the queue endpoints
 * @param {Array}    rows        tracker rows, matched to queue items by assetId
 * @param {Function} onSell      (row, realIndex) → open the sold/traded modal
 * @param {Function} onTrack     (item) → build a row for an item that was never
 *                               tracked, then open the same sale modal on it
 * @param {Array}    inFlight    reconciler report: CSFloat sales that are
 *                               through but still inside trade protection
 * @param {number}   refreshKey  bump to re-read the queue after a sync
 * @param {Function} onStored    an item was answered "in a storage unit", so
 *                               the Locker's storage list has changed
 * @param {string}   variant     'zone' | 'classic'
 */
export default function VanishedItemsPrompt({
  user, rows, onSell, onTrack, inFlight = [], refreshKey = 0, onStored, variant = 'classic',
}) {
  const [queue, setQueue] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!user) { setQueue([]); return; }
    let live = true;
    apiFetch(`/api/tracker/pending-disappearances/${user}`)
      .then(r => (r.ok ? r.json() : { items: [] }))
      .then(d => { if (live) setQueue(d.items || []); })
      .catch(() => {});
    return () => { live = false; };
  }, [user, refreshKey]);

  // A queued assetId means nothing on its own — the row behind it is what
  // carries the cost basis and what a sale would be written onto.
  const entries = useMemo(() => {
    const byAsset = new Map();
    (rows || []).forEach((row, realIndex) => {
      if (!row?.assetId) return;
      // Only rows still open: an item already marked sold by hand answers its
      // own question, and offering to close it again would double-file it.
      if (parseFloat(row.sellPrice) > 0) return;
      if (!byAsset.has(row.assetId)) byAsset.set(row.assetId, { row, realIndex });
    });

    // CSFloat sales that are through but not yet settled, by the assetId the
    // server tied them to. These are not open questions any more — the answer
    // is known, it is just not final enough to write into your books yet.
    const sales = new Map();
    (inFlight || []).forEach(s => { if (s?.assetId) sales.set(s.assetId, s); });

    return (queue || [])
      .filter(i => i?.assetId)
      .map(item => {
        const hit = byAsset.get(item.assetId);
        return {
          item,
          row: hit?.row || null,
          realIndex: hit?.realIndex ?? null,
          inVault: hit ? isVaultRow(hit.row) : false,
          sale: sales.get(item.assetId) || null,
        };
      })
      // Still-open questions first: those are the ones that need you. Explained
      // ones stay listed so the sale is visible, but below the asking. Within
      // each block, vault items lead — the most money and the least likely to be
      // reconstructable from memory later — then oldest question first.
      .sort((a, b) => {
        if (!a.sale !== !b.sale) return a.sale ? 1 : -1;
        if (a.inVault !== b.inVault) return a.inVault ? -1 : 1;
        return new Date(a.item._disappearedAt || 0) - new Date(b.item._disappearedAt || 0);
      });
  }, [queue, rows, inFlight]);

  // What is actually still a question. An item with a CSFloat sale behind it is
  // listed, but it is news rather than a request, and the banner counts only
  // what needs the user.
  const unanswered = useMemo(() => entries.filter(e => !e.sale), [entries]);
  const explained  = entries.length - unanswered.length;

  // `reason` is optional and only 'storage' means anything to the server: keep
  // the item on record as still owned rather than just forgetting the question.
  // The Locker reads that record, so it is told once the write has landed.
  const resolve = useCallback((assetIds, reason) => {
    const ids = new Set(assetIds);
    setQueue(prev => prev.filter(i => !ids.has(i.assetId)));
    if (!user) return;
    apiFetch(`/api/tracker/pending-disappearances-resolve/${user}`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ assetIds: [...ids], ...(reason ? { reason } : {}) }),
    })
      .then(() => { if (reason === 'storage') onStored?.(); })
      .catch(() => {});
  }, [user, onStored]);

  // Opening the sale modal answers the question by itself: whatever the user
  // records in there — a sale or a trade — the item has stopped being a
  // mystery, so the queue entry goes now rather than waiting for a second
  // confirmation the user has no reason to expect.
  const sell = useCallback((row, realIndex, assetId) => {
    setShowModal(false);
    resolve([assetId]);
    onSell?.(row, realIndex);
  }, [onSell, resolve]);

  // Same reasoning for an item that was never tracked: the moment the sale
  // modal opens the disappearance has an explanation, so the queue entry goes.
  const track = useCallback((item) => {
    setShowModal(false);
    resolve([item.assetId]);
    onTrack?.(item);
  }, [onTrack, resolve]);

  if (!entries.length) return null;

  const vaultCount = unanswered.filter(e => e.inVault).length;

  return (
    <>
      {!dismissed && (
        <div style={{ padding: '12px 20px 0' }}>
          <VanishedItemsBanner
            unanswered={unanswered}
            explained={explained}
            vaultCount={vaultCount}
            variant={variant}
            onReview={() => setShowModal(true)}
            onDismiss={() => setDismissed(true)}
          />
        </div>
      )}
      {showModal && (
        <VanishedItemsModal
          items={entries}
          unanswered={unanswered.length}
          variant={variant}
          onSell={sell}
          onTrack={track}
          onResolve={resolve}
          onClose={() => setShowModal(false)}
        />
      )}
    </>
  );
}
